import { useState, useEffect, useRef } from "react";
import useScrollAnimation from "@/hooks/useScrollAnimation";
import { faqData } from "@/mocks/homeData";

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { ref, isVisible } = useScrollAnimation(0.08);
  const schemaInjected = useRef(false);

  useEffect(() => {
    if (schemaInjected.current) return;
    schemaInjected.current = true;

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqData.faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    });
    document.head.appendChild(script);

    return () => {
      const existing = document.head.querySelector(
        'script[type="application/ld+json"]',
      );
      if (existing && existing.textContent?.includes("FAQPage")) {
        existing.remove();
      }
    };
  }, []);

  return (
    <section id="faq" className="w-full py-16 md:py-24 bg-background-50">
      <div
        ref={ref}
        className={`w-full px-4 md:px-6 lg:px-10 transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
          <h2 className="font-heading font-bold text-xl md:text-3xl lg:text-4xl text-foreground-950 mb-4 leading-tight">
            {faqData.headline}
          </h2>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed max-w-2xl mx-auto">
            {faqData.subheadline}
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-3">
          {faqData.faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-background-100 rounded-xl border border-background-200/70 overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-5 md:p-6 text-left cursor-pointer group"
                aria-expanded={openIndex === index}
              >
                <span className="font-heading font-semibold text-sm md:text-base text-foreground-950 pr-4 group-hover:text-primary-600 transition-colors">
                  {faq.question}
                </span>
                <span className="shrink-0 w-8 h-8 rounded-full bg-background-200/70 flex items-center justify-center transition-all group-hover:bg-primary-50">
                  <i
                    className={`ri-${
                      openIndex === index ? "subtract" : "add"
                    }-line text-foreground-600 text-lg transition-transform duration-200`}
                  />
                </span>
              </button>
              {openIndex === index && (
                <div className="px-5 md:px-6 pb-5 md:pb-6 animate-fadeIn">
                  <div className="h-px bg-background-200/70 mb-4" />
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="text-center mt-8">
          <p className="text-sm text-foreground-500">
            Still have questions?{" "}
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="text-primary-600 font-semibold hover:text-primary-500 transition-colors"
            >
              Talk to our team
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}